import moment from "moment-timezone";
import { parsePared } from "../../utils/horaDePared";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useContext, useEffect, useState, useCallback } from "react";
import { getTimezone } from "../../services/settings.api";

/** Una petición por empresa (no una por cada TicketListItem / componente que use el hook). */
const timezoneResolvedByCompany = new Map();
const timezoneInflightByCompany = new Map();

function loadCompanyTimezoneOnce(companyId) {
  if (companyId == null) return Promise.resolve("America/Lima");
  if (timezoneResolvedByCompany.has(companyId)) {
    return Promise.resolve(timezoneResolvedByCompany.get(companyId));
  }
  const existing = timezoneInflightByCompany.get(companyId);
  if (existing) return existing;

  const p = getTimezone()
    .then(({ data }) => {
      const v =
        data?.value && String(data.value).trim() ? data.value : "America/Lima";
      timezoneResolvedByCompany.set(companyId, v);
      return v;
    })
    .catch((error) => {
      console.error("Error fetching company timezone:", error);
      timezoneResolvedByCompany.set(companyId, "America/Lima");
      return "America/Lima";
    })
    .finally(() => {
      timezoneInflightByCompany.delete(companyId);
    });

  timezoneInflightByCompany.set(companyId, p);
  return p;
}

/**
 * ✅ HOOK PARA MANEJO CENTRALIZADO DE ZONA HORARIA
 * 
 * Propósito:
 * - Interpretar fechas "naive" (sin zona horaria) desde el backend
 * - Convertir a la zona horaria configurada de la empresa
 * - Formatear fechas/horas para visualización
 * - Calcular diferencias de tiempo correctamente
 * 
 * Contexto:
 * - Backend guarda fechas como strings "naive": "2025-12-31 13:15:00"
 * - No tienen información de zona horaria (no UTC, no local)
 * - Deben interpretarse en la zona horaria de la empresa
 * 
 * Fuente de timezone:
 * - Settings table (key="timezone", companyId=X) vía API /settings/timezone
 * - Fallback: America/Lima
 */
export function useTimezone() {
  const { user } = useContext(AuthContext);
  const [companyTimezone, setCompanyTimezone] = useState(() =>
    user?.companyId != null && timezoneResolvedByCompany.has(user.companyId)
      ? timezoneResolvedByCompany.get(user.companyId)
      : "America/Lima"
  );

  useEffect(() => {
    let isMounted = true;
    const companyId = user?.companyId;

    if (companyId == null) {
      setCompanyTimezone("America/Lima");
      return () => {
        isMounted = false;
      };
    }

    loadCompanyTimezoneOnce(companyId).then((tz) => {
      if (isMounted) setCompanyTimezone(tz);
    });

    return () => {
      isMounted = false;
    };
  }, [user?.companyId]);

  /**
   * Obtiene la zona horaria configurada
   * Prioridad:
   * 1. Zona horaria de Settings (companyId del usuario)
   * 2. Fallback: America/Lima
   */
  const getConfiguredTimezone = useCallback(() => {
    return companyTimezone;
  }, [companyTimezone]);

  /**
   * Parsea una fecha desde el backend (puede ser naive o ISO)
   *
   * ✅ Regla práctica (consistente con nuestra config de backend):
   * - ISO con Z u offset explícito → instante UTC → convertir a timezone de la empresa
   * - "YYYY-MM-DDTHH:mm:ss" SIN Z (Sequelize/JSON en VPS) → hora NAIVE en TZ empresa, NO usar moment.utc()
   * - Naive "YYYY-MM-DD HH:mm:ss[.SSSSSS]" → hora LOCAL de la empresa (sin shift)
   *
   * Motivo:
   * - En backend usamos MySQL `dateStrings: true` y NO seteamos timezone en Sequelize.
   * - Por lo tanto, MySQL devuelve strings sin TZ que representan la hora local del servidor/empresa.
   */
  const parseNaiveDate = useCallback((dateInput) => {
    if (!dateInput) return null;

    const timezone = companyTimezone;
    let dateString = dateInput;
    
    // Si es un objeto Date, convertirlo a string
    if (dateInput instanceof Date) {
      dateString = dateInput.toISOString();
    } else if (typeof dateInput === 'string') {
      dateString = dateInput;
    } else {
      console.warn("🌍 [parseNaiveDate] Tipo de fecha no reconocido:", typeof dateInput, dateInput);
      return null;
    }
    
    const trimmed = dateString.trim();
    const hasT = trimmed.includes("T");
    /** Solo instantes UTC reales: Z o offset al final (+05:00, -0300, Z) */
    const hasExplicitUtcMarker =
      trimmed.includes("Z") ||
      /[+-]\d{2}:?\d{2}$/.test(trimmed) ||
      /[+-]\d{4}$/.test(trimmed);

    if (hasT && hasExplicitUtcMarker) {
      const m = moment.utc(trimmed);
      if (m.isValid()) {
        return m.tz(timezone);
      }
    }

    // "2026-05-10T05:09:52" sin Z — backend siempre almacena UTC; interpretar como UTC
    if (hasT && !hasExplicitUtcMarker) {
      const naiveLike = trimmed.replace("T", " ");
      const strictFormats = [
        "YYYY-MM-DD HH:mm:ss.SSSSSS",
        "YYYY-MM-DD HH:mm:ss.SSS",
        "YYYY-MM-DD HH:mm:ss"
      ];
      // Backend siempre almacena UTC (UtcDateHelper.nowStorage); interpretar como UTC
      for (let i = 0; i < strictFormats.length; i += 1) {
        const m = moment.utc(naiveLike, strictFormats[i], true);
        if (m.isValid()) return m.tz(timezone);
      }
      const loose = moment.utc(naiveLike);
      if (loose.isValid()) return loose.tz(timezone);
    }

    if (!hasT) {
      const naivePattern = /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})(\.\d+)?$/;
      const match = trimmed.match(naivePattern);

      if (match) {
        // MySQL DATETIME con dateStrings:true devuelve UTC naive — interpretar como UTC
        for (const fmt of ["YYYY-MM-DD HH:mm:ss.SSSSSS", "YYYY-MM-DD HH:mm:ss.SSS", "YYYY-MM-DD HH:mm:ss"]) {
          const m = moment.utc(trimmed, fmt, true);
          if (m.isValid()) return m.tz(timezone);
        }
        const loose = moment.utc(trimmed);
        if (loose.isValid()) return loose.tz(timezone);
      } else {
        const m = moment.utc(trimmed);
        if (m.isValid()) return m.tz(timezone);
      }
    }
    
    console.warn("Error parseando fecha:", dateString);
    return null;
  }, [companyTimezone]);

  /**
   * Formatea una fecha naive como hora (HH:mm)
   * @param {string} naiveString - Fecha en formato "YYYY-MM-DD HH:mm:ss"
   * @returns {string} - Hora formateada "HH:mm"
   */
  const formatTime = useCallback((dateInput) => {
    const m = parseNaiveDate(dateInput);
    if (!m || !m.isValid()) {
      return "";
    }
    return m.format("HH:mm");
  }, [parseNaiveDate, companyTimezone]);

  /**
   * Formatea una fecha naive como fecha completa (DD/MM/YYYY HH:mm)
   * @param {string} naiveString - Fecha en formato "YYYY-MM-DD HH:mm:ss"
   * @returns {string} - Fecha formateada "DD/MM/YYYY HH:mm"
   */
  const formatDateTime = useCallback((naiveString) => {
    const m = parseNaiveDate(naiveString);
    if (!m || !m.isValid()) return "";
    return m.format("DD/MM/YYYY HH:mm");
  }, [parseNaiveDate]);

  /** Igual que formatDateTime pero año en 2 dígitos (panel / listas densas). */
  const formatDateTimeShortYear = useCallback((dateInput) => {
    const m = parseNaiveDate(dateInput);
    if (!m || !m.isValid()) return "";
    return m.format("DD/MM/YY HH:mm");
  }, [parseNaiveDate]);

  /**
   * Formatea una fecha naive como fecha (DD/MM/YYYY)
   * @param {string} naiveString - Fecha en formato "YYYY-MM-DD HH:mm:ss"
   * @returns {string} - Fecha formateada "DD/MM/YYYY"
   */
  const formatDate = useCallback((naiveString) => {
    const m = parseNaiveDate(naiveString);
    if (!m || !m.isValid()) return "";
    return m.format("DD/MM/YYYY");
  }, [parseNaiveDate]);

  /**
   * Calcula "hace cuánto tiempo" desde una fecha naive
   * @param {string} naiveString - Fecha en formato "YYYY-MM-DD HH:mm:ss"
   * @returns {object} - { labelText: string, labelColor: string }
   * 
   * Retorna texto y color según la antigüedad:
   * - < 1 min: "Hace unos momentos" (verde)
   * - 1-10 min: "Hace X minutos" (verde)
   * - 10-30 min: "Hace X minutos" (amarillo)
   * - 30-60 min: "Hace X minutos" (rojo)
   * - 1-24 hrs: "Hace X horas" (rojo)
   * - 1+ días: "Hace X días" (rojo oscuro)
   */
  const getTimeAgo = useCallback((naiveString) => {
    const m = parseNaiveDate(naiveString);
    if (!m || !m.isValid()) {
      return { labelText: '', labelColor: '#6b7280' };
    }

    const timezone = companyTimezone;
    const now = moment().tz(timezone);
    
    const minutesDiff = now.diff(m, 'minutes');
    const hoursDiff = now.diff(m, 'hours');
    const daysDiff = now.diff(m, 'days');

    let labelText = '';
    let labelColor = '';

    if (minutesDiff < 1) {
      labelText = 'ahora';
      labelColor = '#10b981';
    } else if (minutesDiff < 60) {
      labelText = `hace ${minutesDiff}m`;
      labelColor = minutesDiff <= 10 ? '#10b981' : minutesDiff < 30 ? '#f59e0b' : '#ef4444';
    } else if (hoursDiff < 24) {
      labelText = `hace ${hoursDiff}h`;
      labelColor = '#ef4444';
    } else {
      labelText = `hace ${daysDiff}d`;
      labelColor = '#dc2626';
    }

    return { labelText, labelColor };
  }, [parseNaiveDate, companyTimezone]);

  /**
   * Obtiene la hora actual en la zona horaria configurada
   * @returns {moment.Moment} - Momento actual en la zona horaria configurada
   */
  const getNow = useCallback(() => {
    const timezone = companyTimezone;
    return moment().tz(timezone);
  }, [companyTimezone]);

  /**
   * 🔴 HAY DOS CLASES DE FECHA EN ESTE SISTEMA, Y CONFUNDIRLAS ES EL FALLO QUE MÁS
   * TIEMPO HA DURADO.
   *
   *   · INSTANTE  — `createdAt`, `updatedAt`, `deliveredAt`, `sentAt`…
   *     Un momento del tiempo. Se guarda en UTC y hay que CONVERTIRLO a la zona de
   *     la empresa para mostrarlo. De eso se encarga `formatDateTime`.
   *
   *   · HORA DE PARED — `scheduledAt`, `scheduledDate`, `nextScheduledAt`,
   *     `recurrenceEndDate`, `appointmentDate`.
   *     Lo que el cliente escribió en el formulario: «las 09:00». **No se convierte
   *     nunca**, porque ya está en su zona. Convertirla la desplaza.
   *
   * `parseNaiveDate` asume que TODO lo que llega es UTC —lo dice en sus comentarios:
   * «el backend siempre almacena UTC»—, y para los campos de pared eso es falso. El
   * resultado, medido el 2026-09-12: la base tenía `2026-09-14 09:34` de Shanghái y la
   * lista mostraba `17:34`. Ocho horas.
   *
   * Y el síntoma de que faltaba esta distinción estaba a la vista: cada pantalla que
   * chocó con ello se escribió **su propio rodeo** —`formatCampaignScheduledDate`,
   * `formatGroupScheduledDate`—, la misma función copiada dos veces. Esta es la que
   * las sustituye, y se llama por lo que hace para que nadie tenga que adivinarlo.
   *
   * Ver doc 0412 §11.
   */
  // La lógica vive en `utils/horaDePared`: la comparten los componentes (por este hook)
  // y las funciones puras de validación, que no pueden usar hooks. Sin ese sitio común
  // cada uno se escribía el suyo — llegó a haber SEIS copias.
  const parseHoraDePared = useCallback(
    (valor) => parsePared(valor, companyTimezone),
    [companyTimezone]
  );

  const formatHoraDePared = useCallback(
    (valor) => {
      const m = parseHoraDePared(valor);
      return m ? m.format("DD/MM/YYYY HH:mm") : "";
    },
    [parseHoraDePared]
  );

  return {
    parseNaiveDate,
    formatTime,
    formatDateTime,
    formatDateTimeShortYear,
    formatDate,
    parseHoraDePared,
    formatHoraDePared,
    getTimeAgo,
    getNow,
    getConfiguredTimezone
  };
}
